export type AssessmentRelationValue =
    | number
    | string
    | {
          id?: number | string;
          name?: string;
          title?: string;
          label?: string;
          level?: number | string;
      }
    | null
    | undefined;

export interface AssessmentRecord {
    id: number;
    dimensionId?: number | null;
    levelId?: number | null;
    dimension?: AssessmentRelationValue;
    level?: AssessmentRelationValue;
    title: string;
    description: string;
    content: string;
    video: string;
    pic: string;
    uid?: string;
    status: number;
}

export interface AssessmentDimensionOption {
    id: number;
    dimension: string;
}

export interface AssessmentDimensionDetail {
    id?: number;
    dimension: string;
    idx: number;
}

export interface AssessmentLevelOption {
    id: number;
    level: number;
}

export interface AssessmentLevelDetail {
    id?: number;
    level: number;
    ageMin: number;
    ageMax: number;
}

const toFiniteNumber = (value: unknown) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
};

const getRelationObject = (value: AssessmentRelationValue) => {
    if (typeof value === 'object' && value !== null) {
        return value;
    }

    return undefined;
};

export const getAssessmentDimensionId = (record: Partial<AssessmentRecord>) => {
    const directId = toFiniteNumber(record.dimensionId);
    if (directId !== undefined) return directId;

    return toFiniteNumber(getRelationObject(record.dimension)?.id);
};

export const getAssessmentLevelId = (record: Partial<AssessmentRecord>) => {
    const directId = toFiniteNumber(record.levelId);
    if (directId !== undefined) return directId;

    const nestedId = toFiniteNumber(getRelationObject(record.level)?.id);
    if (nestedId !== undefined) return nestedId;

    if (typeof record.level === 'number') {
        return record.level;
    }

    return undefined;
};

export const getAssessmentDimensionLabel = (record: Partial<AssessmentRecord>) => {
    const relation = getRelationObject(record.dimension);

    if (relation) {
        const relationLabel = relation.name || relation.title || relation.label;
        if (relationLabel) return relationLabel;
    }

    if (typeof record.dimension === 'string' && record.dimension.trim()) {
        return record.dimension;
    }

    const dimensionId = getAssessmentDimensionId(record);
    return dimensionId !== undefined ? String(dimensionId) : '';
};

export const getAssessmentLevelLabel = (record: Partial<AssessmentRecord>) => {
    const relation = getRelationObject(record.level);

    if (relation) {
        if (relation.level !== undefined && relation.level !== null) {
            return String(relation.level);
        }

        const relationLabel = relation.name || relation.title || relation.label;
        if (relationLabel) return relationLabel;
    }

    if (typeof record.level === 'number') {
        return String(record.level);
    }

    const levelId = getAssessmentLevelId(record);
    return levelId !== undefined ? String(levelId) : '';
};